import { LOADING, PROFILE, AUTHENTICATED, ROLES, LOGINFAILE, COMPLETE_USER_DATA, CHECK_SUBSCRIPTIONS, USER_ACTIVE_PLAN, SAVINGS_PLAN_ELEMENTS_STATUS } from "./Constants";
import Auth from "@/Auth/Auth";
import UserService from "@/services/UserService";
import ElementService, { SavingsPlanElementsStatus } from "@/services/ElementsService";
import { CLEAR_CART } from "@/store/cart/Constants";
import type { AppThunk } from "@/store/thunk";
import { sanitizeUserProfileForStorage } from "@/utils/sanitizeUserProfile";

export const loginRedux = (respuesta: any): AppThunk => async (dispatch) =>
{
    Auth.setSession(respuesta);
    dispatch(
    {
        // El payload crudo trae credenciales del interviniente (códigos de firma incluidos): al store solo va el allowlist.
        type: PROFILE,
        payload: sanitizeUserProfileForStorage(respuesta),
    });
};

export const completeUserDataRedux = (userDruId: string): AppThunk => async (dispatch) =>
{
    await UserService.getUserByDruid(userDruId).then((data) =>
    {
        Auth.setUserProfile(data);
        dispatch(
        {
            type: COMPLETE_USER_DATA,
            payload: sanitizeUserProfileForStorage(data),
        });
    })
    .catch(() => {});
};

const normalizeActivePlan = (plan: any) =>
{
    return{
        ...plan,
        id: plan.planId ?? plan.id,
        name: plan.planName ?? plan.name,
        statusName: plan.status ?? plan.statusName,
        sync: plan.sync ?? true,
    };
};

/**
 * Id del plan tolerando las dos formas del payload (planId / id) y que llegue como cadena.
 * Devuelve undefined si no se puede determinar, para no tratar "desconocido" como "distinto".
 */
const resolveActivePlanId = (plan: any): number | undefined =>
{
    const rawId = plan?.planId ?? plan?.id;
    if(rawId === undefined || rawId === null || rawId === "")
    {
        return undefined;
    }

    const parsedId = Number(rawId);
    return Number.isNaN(parsedId) ? undefined : parsedId;
};

export const setActiveSavingsPlan = (savingsPlan: any): AppThunk => async (dispatch, getState) =>
{
    const normalized = normalizeActivePlan(savingsPlan);
    const previousPlanId = resolveActivePlanId(getState().auth?.userActivePlan);
    const nextPlanId = resolveActivePlanId(normalized);

    await dispatch(
    {
        type: USER_ACTIVE_PLAN,
        payload: normalized,
    });

    // El carrito es único y global (clave "cart" de localStorage): pertenece al plan que
    // estuviera activo. Por eso solo hay que vaciarlo cuando el plan REALMENTE cambia; si se
    // vacía siempre, se pierde el carrito guardado en dos casos que no lo justifican:
    //   - la hidratación del arranque (userLastPlanActive), donde no hay plan previo;
    //   - las pantallas que reafirman el MISMO plan activo (p. ej. limpiar outdatedContract
    //     tras firmar la nueva versión del contrato en ConvenienceTestConfirm).
    // Sin plan previo o sin id nuevo no podemos afirmar que haya cambio: no se limpia.
    const planHasChanged = previousPlanId !== undefined && nextPlanId !== undefined && previousPlanId !== nextPlanId;

    if(planHasChanged)
    {
        await dispatch(
        {
            type: CLEAR_CART,
        });
    }
};

export const userLastPlanActive = (userId: any): AppThunk => async (dispatch) =>
{
    await UserService.getUserLastActivePlan(userId).then(async (data) =>
    {
        Auth.setActivePlan(data);
        // Hidratamos el slice en el arranque. Antes se invocaba el thunk SIN dispatch, así que
        // state.auth.userActivePlan nunca se rellenaba al iniciar sesión y las pantallas que lo
        // leen (aviso de contrato caducado del Header, filtrado del MainMenu) no reaccionaban
        // hasta que otra pantalla lo hidrataba por su cuenta. Ahora sí se despacha; el carrito
        // no corre peligro porque setActiveSavingsPlan solo lo limpia si el plan cambia.
        await dispatch(setActiveSavingsPlan({ ...data, sync: true }));
    })
    .catch(() => {});
};

export const setSavingsPlanElementsStatus = (savingsPlanId: number): AppThunk => (dispatch) =>
{
    if(savingsPlanId !== undefined)
    {
        ElementService.isSavingsPlanSubscribedToElements(savingsPlanId).then(async (savingsPlanElementsStatus: SavingsPlanElementsStatus) =>
        {
            await dispatch(
            {
                type: SAVINGS_PLAN_ELEMENTS_STATUS,
                payload: savingsPlanElementsStatus,
            });
            Auth.setSavingsPlanElementsStatus(
            {
                ...savingsPlanElementsStatus,
                sync: true,
            });
        })
        .catch(() => {});
    }
};

export const checkSuscriptionsRedux = (userId: number): AppThunk => async (dispatch) =>
{
    await UserService.getSuscriptions(userId).then((data) =>
    {
        const user = Auth.getUserProfile();
        Auth.setUserProfile({ ...user, usersSubscriptions: data });
        dispatch(
        {
            type: CHECK_SUBSCRIPTIONS,
            payload: data,
        });
    })
    .catch(() => {});
};

export const restorePasswordRedux = (_email: string): AppThunk => async (dispatch) =>
{
    dispatch(
    {
        type: LOADING,
        payload: true,
    });
};

export const resetProfileRedux = (): AppThunk => async (dispatch) =>
{
    localStorage.removeItem("access_token");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("profile");
    localStorage.removeItem("roles");

    dispatch(
    {
        type: PROFILE,
        payload: [],
    });
    dispatch(
    {
        type: AUTHENTICATED,
        payload: false,
    });
    dispatch(
    {
        type: ROLES,
        payload: [],
    });
};

export const loginReduxError = (): AppThunk => async (dispatch) =>
{
    dispatch(
    {
        type: LOGINFAILE,
        payload: true,
    });
};

export const isAuthenticateRedux = (value: boolean): AppThunk => async (dispatch) =>
{
    dispatch(
    {
        type: AUTHENTICATED,
        payload: value,
    });
};